/**
 * This entry point is only used for embedded scratchpads.
 */
import "../../stylesheets/shared-package/shared.less";
import "../../stylesheets/moderation-package/moderation.less";
import "../../stylesheets/discussion-package/discussion.less";
import "../../stylesheets/avatar-customizer-package/avatar-customizer.less";
import "../../stylesheets/odometer-package/odometer.less";
import "../../stylesheets/tasks-package/tasks.less";
import "../../stylesheets/scratchpad-challenge-package/scratchpad-challenge.less";
import "../../stylesheets/scratchpads-package/scratchpads.less";
import "../../stylesheets/intro-package/intro.less";
// TODO(joel): Extract the discussions related stuff out of the video package
import "../../stylesheets/video-package/video.less";
// TODO(pamelafox): Extract into a separate package
import "../../stylesheets/clarifications-package/clarifications.less";

import * as React from "react";
import {useParams} from "react-router";

import ErrorLoading from "#app-shell-package/errors/error-loading-page.tsx";
import PageRoot from "#app-shell-package/page-root.tsx";
import EmbeddedPageTemplate from "#app-shell-package/page-templates/embedded-page-template.tsx";
import EmbeddedRouteLoadSpinner from "#app-shell-package/page-templates/embedded-route-load-spinner.tsx";
import useProfile from "#data-access/use-profile.ts";
import {useUpdatePageSettings} from "#data-access/use-update-page-settings.ts";
import {ScratchpadUIStart} from "#scratchpads-package/scratchpad-ui-start.ts";
import {ScratchpadUI} from "#scratchpads-package/scratchpad-ui.tsx";
import {getTermMap} from "#scratchpads-package/user-authored-content-types.ts";

import {withStrings} from "./locales/embedded-scratchpad-page/load-strings.tsx";
import {useProgram} from "./use-program.ts";

const EmbeddedScratchpadPage = (): React.ReactElement => {
    const userProfile = useProfile();
    // @ts-expect-error - TS2339 - Property 'scratchpad_id' does not exist on type '{}'.
    const {scratchpad_id: scratchpadId} = useParams();
    const scratchpadOptionsResult = useProgram(scratchpadId);

    const title =
        scratchpadOptionsResult.status === "success"
            ? (scratchpadOptionsResult.data.scratchpad.translatedTitle ?? "")
            : "";

    useUpdatePageSettings({
        title,
    });

    React.useEffect(() => {
        if (
            userProfile.status !== "loading" &&
            scratchpadOptionsResult.status === "success"
        ) {
            const options = scratchpadOptionsResult.data;
            const {userAuthoredContentType} = options.scratchpad;

            const init = async () => {
                const {profile} = userProfile;
                ScratchpadUI.termMap = getTermMap(userAuthoredContentType);
                await ScratchpadUIStart();
                ScratchpadUI.init({
                    ...options,
                    profile,
                    userAuthoredContentType,
                });
            };

            init();
        }
    }, [userProfile, scratchpadOptionsResult]);

    React.useEffect(() => {
        // Remove Scratchpad UI when this component is unmounted.
        // To avoids talk-throughs from continuing to play when
        // navigating away from a page.
        return () => ScratchpadUI.triggerRemove();
    }, []);

    if (
        scratchpadOptionsResult.status === "loading" ||
        scratchpadOptionsResult.status === "no-data"
    ) {
        return <EmbeddedRouteLoadSpinner />;
    }

    if (
        scratchpadOptionsResult.status === "error" ||
        scratchpadOptionsResult.status === "aborted"
    ) {
        return <ErrorLoading />;
    }

    return (
        <PageRoot template={EmbeddedPageTemplate}>
            <div className="scratchpad-page embedded" />
        </PageRoot>
    );
};

export default withStrings(EmbeddedScratchpadPage);
